import React, { useState, useRef, useEffect } from "react";
import UserServiceService from "../../Services/userServiceService";
import styles from "./UserServices.module.css";
import i18n from "../../Services/i18n";
import UserServiceQuickForm from "../../Components/QuickFormComponents/UserServiceQuickForm/UserServiceQuickForm";
import { useToasts } from "../../LayoutComponents/ToastContext";
import UserServiceInfoTab from "../../Components/UserServiceTabs/UserServicesInfoTab";
import { useUserServices } from "../../Context/UserServiceContext";
import LoadingSpinner from "../../LayoutComponents/LoadingSpinner";
import ListCardContainer from "../../Components/ListCard/ListCardContainer";
import { useModal } from "../../Context/ModalContext";
import PrivilegeValidationService from "../../Services/privilegeValidationService";
import { usePrivileges } from "../../Context/PrivilegeContext";

const UserServices = () => {
  const serviceListRef = useRef({});
  const serviceOccurenceListRef = useRef({});
  const Tabs = {
    INFO: "Infos",
    SERVICES: "Services reçus",
  };

  const [loadedUserServiceId, setLoadedUserServiceId] = useState();
  const [isLoadingServiceDetail, setIsLoadingServiceDetail] = useState(false);
  const [selectedUserServicesDetail, setSelectedUserServiceDetail] = useState();
  const [selectedService, setSelectedService] = useState();
  const [activeTab, setActiveTab] = useState(Tabs.INFO);
  const [formKeys, setFormKeys] = useState({ formService: 0 });
  const [isLocked, setIsLocked] = useState(false);
  const { privileges } = usePrivileges();

  const { addToast } = useToasts();
  const { userServicesData, isFetchingUserServices, fetchUserServices } = useUserServices();
  const { openModal } = useModal();

  useEffect(() => {
    if (loadedUserServiceId) {
      const svc = userServicesData.find((x) => x.externalId === loadedUserServiceId);
      if (svc) {
        handleSelectService(svc);
      } else {
        addToast(i18n.t("Generic.NotFoundError"), "CLIENTNOTFOUND", "ERROR", true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedUserServiceId, userServicesData]);

  const handleAddUserServiceClick = async () => {
    if (
      !PrivilegeValidationService.CanAddUserService(await privileges(), userServicesData?.length)
    ) {
      addToast(
        i18n.t("Privilege.ValidationError.MaxUserServices"),
        "PrivilegeValidationFailed",
        "ERROR",
        true,
      );
      return;
    }
    openModal({
      title: i18n.t("ServicePage.CreateServiceHeader"),
      form: "formService",
      hideSaveButton: true,
      hideSaveAndCloseButton: false,
      wideMode: true,
      canClose: async () => {
        const isDirty = quickAddUserServiceForm.current?.isDirty?.();
        if (!isDirty) return true;

        return window.confirm(i18n.t("Modal.UnsavedChangesConfirm"));
      },
      content: (
        <UserServiceQuickForm
          key={formKeys.formService}
          ref={(ref) => {
            if (ref) quickAddUserServiceForm.current = ref;
          }}
        />
      ),
      onSave: async () => {
        try {
          const isValid = quickAddUserServiceForm.current.validate();
          if (!isValid) return;

          const id = await UserServiceService.QuickCreate(
            quickAddUserServiceForm.current.getFormData(),
          );

          if (!id) return false;

          await refreshUserServiceList(id);
          return true;
        } catch (ex) {
          console.log(ex);
          addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
          return false;
        }
      },
      onClose: () => {
        handleCloseForm("formService");
      },
    });
  };

  const quickAddUserServiceForm = useRef(null);

  const refreshUserServiceList = async (userServiceExternalId) => {
    try {
      await fetchUserServices();
      if (userServiceExternalId) {
        setLoadedUserServiceId(userServiceExternalId);
      } else {
        setLoadedUserServiceId(undefined);
        setSelectedService(undefined);
        setSelectedUserServiceDetail(undefined);
      }
    } catch (ex) {
      addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
    }
  };

  const handleSelectService = async (service, refreshServices = false) => {
    try {
      if (
        !refreshServices &&
        selectedService &&
        service.externalId === selectedService.externalId
      ) {
        return;
      }

      setIsLoadingServiceDetail(true);
      const userServiceDetail = await UserServiceService.GetDetailUserService(service.externalId);
      setSelectedUserServiceDetail(userServiceDetail);
      setSelectedService(service);
      setIsLoadingServiceDetail(false);
    } catch (ex) {
      console.log(ex);
      addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
    }
  };

  const renderTabs = () => (
    <div className={styles.tabs}>
      {Object.values(Tabs).map((tab) => {
        const isDisabled = isLocked && tab !== Tabs.INFO;
        return (
          <div
            key={tab}
            style={{
              transition:
                "background-color 0.2s ease, " +
                "border-bottom 0.2s ease,  border-top 0.2s ease, border-left 0.2s ease,  border-right 0.2s ease, " +
                "font-weight 0.2s ease, padding 0.2s ease",
            }}
            className={
              activeTab === tab ? styles.activeTab : isDisabled ? styles.tabDisabled : styles.tab
            }
            onClick={() => !isDisabled && setActiveTab(tab)}>
            {tab}
          </div>
        );
      })}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case Tabs.INFO:
        return (
          <div>
            {selectedService && (
              <UserServiceInfoTab
                userService={selectedUserServicesDetail}
                lockParent={(v) => {
                  setIsLocked(v);
                }}
                reloadDetailUserService={(service) => {
                  handleSelectService(service);
                }}
              />
            )}
          </div>
        );
      case Tabs.SERVICES:
        return (
          <>
            {selectedService && (
              <ListCardContainer
                hideHeader={true}
                isLoading={false}
                hideEmptyHorseCard={true}
                refresh={() => {
                  handleSelectService(selectedService, true);
                }}
                dataItems={selectedUserServicesDetail?.serviceOccurences ?? []}
                onSelect={() => {}}
                selectedItemExternalId={selectedUserServicesDetail.externalId}
                type="serviceoccurence"
                ref={serviceOccurenceListRef}
                storageId={"UserServicePage.Service"}
              />
            )}
          </>
        );
      default:
        return <div></div>;
    }
  };

  const renderUserServiceOccurenceDetail = () => {
    if (!selectedService && isLoadingServiceDetail) {
      return (
        <div className={styles.tabContent}>
          <div className={styles.loadingIndicator}>
            <LoadingSpinner />
          </div>
        </div>
      );
    }

    if (!selectedService && !isLoadingServiceDetail) {
      return <div className={styles.centerMessage}>{i18n.t("ServicePage.PleaseSelect")}</div>;
    }

    if (isLoadingServiceDetail) {
      return (
        <div className={styles.tabContent}>
          <div className={styles.loadingIndicator}>
            <LoadingSpinner />
          </div>
        </div>
      );
    } else {
      return <div className={styles.tabContent}>{renderTabContent()}</div>;
    }
  };

  const renderUserServiceOccurences = () => {
    return (
      <div
        className={`${styles.userServicesLastOccurenceSection} ${!selectedService ? styles.faded : ""}`}>
        <div className={styles.boardSectionHeader}>
          <div
            className={styles.headerContainer}
            style={{ paddingTop: "5px", paddingBottom: "5px" }}>
            {selectedService ? <span>{selectedService.title}</span> : <span>...</span>}
          </div>
        </div>

        {renderTabs()}
        {renderUserServiceOccurenceDetail()}
      </div>
    );
  };

  const handleCloseForm = (formName) => {
    setFormKeys((prevKeys) => ({
      ...prevKeys,
      [formName]: prevKeys[formName] + 1,
    }));
  };

  return (
    <div className={styles.container}>
      <div
        className={`${styles.userServicesListSection} ${isLocked ? styles.userServicesListSectionDisabled : ""}`}>
        <ListCardContainer
          addButtonAction={handleAddUserServiceClick}
          headerLabel={i18n.t("ServicePage.ServiceHeader")}
          isLoading={isFetchingUserServices}
          dataItems={userServicesData ?? []}
          onSelect={handleSelectService}
          selectedItemExternalId={selectedService?.externalId}
          refresh={async () => await refreshUserServiceList()}
          type="userservice"
          ref={serviceListRef}
          storageId={"UserServicePage.ServiceOccurence"}
        />
      </div>
      {renderUserServiceOccurences()}
    </div>
  );
};

export default UserServices;
